import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ClockIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { timeConditionsAPI, timeGroupsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';

const TimeConditionsPage = () => {
  const [timeConditions, setTimeConditions] = useState([]);
  const [timeGroups, setTimeGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('conditions');
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null, type: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [conditionsRes, groupsRes] = await Promise.all([
        timeConditionsAPI.getAll(),
        timeGroupsAPI.getAll(),
      ]);
      setTimeConditions(conditionsRes.data?.data || conditionsRes.data || []);
      setTimeGroups(groupsRes.data?.data || groupsRes.data || []);
    } catch (error) {
      toast.error('Failed to load time conditions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.item) return;
    try {
      if (deleteModal.type === 'condition') {
        await timeConditionsAPI.delete(deleteModal.item.id || deleteModal.item.extension);
      } else {
        await timeGroupsAPI.delete(deleteModal.item.id);
      }
      toast.success('Deleted successfully');
      setDeleteModal({ open: false, item: null, type: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Time-Based Routing</h1>
          <p className="text-slate-400 mt-1">Configure call routing based on time and schedules</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-800 pb-2">
        <button
          onClick={() => setActiveTab('conditions')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'conditions' 
              ? 'bg-primary-500/20 text-primary-400' 
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <ClockIcon className="w-4 h-4 inline mr-2" />
          Time Conditions
        </button>
        <button
          onClick={() => setActiveTab('groups')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'groups' 
              ? 'bg-primary-500/20 text-primary-400' 
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <CalendarIcon className="w-4 h-4 inline mr-2" />
          Time Groups
        </button>
      </div>

      {activeTab === 'conditions' ? (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button className="btn-primary" onClick={() => toast('Time condition creation coming soon')}>
              <PlusIcon className="w-5 h-5 mr-2" />Create Time Condition
            </button>
          </div>
          
          {timeConditions.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {timeConditions.map((condition) => (
                <div key={condition.id || condition.extension} className="card hover:border-primary-500/30">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-lg bg-blue-500/20">
                        <ClockIcon className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">{condition.name}</h3>
                        <p className="text-sm text-primary-400 font-mono">{condition.extension}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button className="p-2 text-slate-400 hover:text-primary-400 rounded-lg hover:bg-slate-800">
                        <PencilSquareIcon className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => setDeleteModal({ open: true, item: condition, type: 'condition' })}
                        className="p-2 text-slate-400 hover:text-red-400 rounded-lg hover:bg-slate-800"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-800 text-sm">
                    <p className="text-slate-500">Time Group: <span className="text-slate-300">{condition.time_group || 'Not set'}</span></p>
                    <p className="text-slate-500">Match: <span className="text-green-400">{condition.match_destination || 'Not configured'}</span></p>
                    <p className="text-slate-500">No Match: <span className="text-yellow-400">{condition.nomatch_destination || 'Not configured'}</span></p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card text-center py-12">
              <ClockIcon className="w-12 h-12 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">No time conditions configured</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button className="btn-primary" onClick={() => toast('Time group creation coming soon')}>
              <PlusIcon className="w-5 h-5 mr-2" />Create Time Group
            </button>
          </div>
          
          {timeGroups.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Schedule</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {timeGroups.map((group) => (
                    <tr key={group.id}>
                      <td className="text-white font-medium">{group.name}</td>
                      <td className="text-slate-400">{group.description || group.schedule || 'Custom schedule'}</td>
                      <td className="text-right">
                        <button 
                          onClick={() => setDeleteModal({ open: true, item: group, type: 'group' })}
                          className="p-2 text-slate-400 hover:text-red-400 rounded-lg hover:bg-slate-800"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card text-center py-12">
              <CalendarIcon className="w-12 h-12 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">No time groups configured</p>
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, item: null, type: null })}
        onConfirm={handleDelete}
        title="Delete Item"
        message="Are you sure you want to delete this item?"
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};

export default TimeConditionsPage;
